import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useInstagramConnect } from './useInstagramConnect';
import { getInstagramStatus } from '@/services/instagram.service';
import { queryKeys } from '@/hooks/queries/keys';

const POLL_INTERVAL_MS = 2500;
const TIMEOUT_MS = 5 * 60 * 1000;

interface State {
    isOpening: boolean;
    isPolling: boolean;
}

export const useInstagramOAuthFlow = () => {
    const queryClient = useQueryClient();
    const connect = useInstagramConnect();
    const [state, setState] = useState<State>({ isOpening: false, isPolling: false });

    const popupRef = useRef<Window | null>(null);
    const pollTimerRef = useRef<number | null>(null);
    const timeoutTimerRef = useRef<number | null>(null);

    const cleanup = () => {
        if (pollTimerRef.current !== null) {
            window.clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        if (timeoutTimerRef.current !== null) {
            window.clearTimeout(timeoutTimerRef.current);
            timeoutTimerRef.current = null;
        }
    };

    const stop = (reason: 'success' | 'closed' | 'timeout') => {
        cleanup();
        if (popupRef.current && !popupRef.current.closed) {
            try {
                popupRef.current.close();
            } catch {
                /* noop */
            }
        }
        popupRef.current = null;
        setState({ isOpening: false, isPolling: false });

        queryClient.invalidateQueries({ queryKey: queryKeys.instagram.all });

        if (reason === 'success') toast.success('Instagram 계정이 연결됐어요');
        if (reason === 'timeout') toast.error('연결 시간이 초과됐어요. 다시 시도해주세요');
    };

    const start = async () => {
        if (state.isOpening || state.isPolling) return;
        setState({ isOpening: true, isPolling: false });

        try {
            const { redirectUrl } = await connect.mutateAsync();

            const popup = window.open(
                redirectUrl,
                'instagram_oauth',
                'popup,width=520,height=720,noopener=no',
            );

            if (!popup) {
                setState({ isOpening: false, isPolling: false });
                toast.error('팝업이 차단됐어요. 브라우저 설정을 확인해주세요');
                return;
            }

            popupRef.current = popup;
            setState({ isOpening: false, isPolling: true });

            pollTimerRef.current = window.setInterval(async () => {
                if (popupRef.current?.closed) {
                    try {
                        const status = await getInstagramStatus(true);
                        queryClient.setQueryData(queryKeys.instagram.status(true), status);
                        stop(status.isConnected ? 'success' : 'closed');
                    } catch {
                        stop('closed');
                    }
                    return;
                }

                try {
                    const status = await getInstagramStatus(true);
                    queryClient.setQueryData(queryKeys.instagram.status(true), status);
                    if (status.isConnected) stop('success');
                } catch {
                    /* 폴링 중 일시적 오류는 무시하고 다음 tick 재시도 */
                }
            }, POLL_INTERVAL_MS);

            timeoutTimerRef.current = window.setTimeout(() => stop('timeout'), TIMEOUT_MS);
        } catch (err: any) {
            setState({ isOpening: false, isPolling: false });
            const message = err?.data?.message || err?.message || 'Instagram 연결을 시작하지 못했어요';
            toast.error(typeof message === 'string' ? message : 'Instagram 연결을 시작하지 못했어요');
        }
    };

    useEffect(() => () => cleanup(), []);

    return {
        start,
        isOpening: state.isOpening,
        isPolling: state.isPolling,
        isBusy: state.isOpening || state.isPolling,
    };
};
